import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { API_SERVICE } from "../Services/CommonApi";
import {
  GET_BUDGETS,
  ADD_EDIT_BUDGET,
  DELETE_BUDGET,
  CHECK_PO,
  REQUEST_OVERRIDE,
  GET_PENDING_OVERRIDES,
  DECIDE_OVERRIDE,
  GET_OVERRIDES,
} from "../constant/CommonApiUrl";

// Envelope is lowercase (data/status/messageCode), confirmed against a live
// GetBudgets response — read response.data.data/.status/.messageCode directly.

const BUDGET_MESSAGES = {
  INVALID_PERIOD: "Period start must be before period end.",
  INVALID_AMOUNT: "Allocated amount must be greater than zero.",
  MISSING_APPROVER: "Please select an override approver.",
  DUPLICATE_SCOPE: "An active budget already exists for this department and period.",
  // NOT_FOUND omitted: RequestOverride/DecideOverride use it with different
  // meanings — fall through to the server's own message instead.
  NO_EXCEED: "Override no longer needed — PO is within budget.",
  PO_NOT_FOUND: "PO not found.",
  BUDGET_MISMATCH: "This PO belongs to a different budget — refresh and try again.",
  NOT_AUTHORIZED: "Only the assigned override approver can decide this request.",
  INVALID_DECISION: "Invalid decision.",
  BUDGET_OVERRIDE_REQUIRED: "This PO exceeds its department budget and needs an approved override before it can be approved.",
  BUDGET_CHECK_FAILED: "Unable to verify this PO's budget status right now. Please retry, or contact support if this persists.",
};

export const getBudgetMessage = (messageCode, fallbackMessage) =>
  BUDGET_MESSAGES[messageCode] || fallbackMessage || "Something went wrong.";

const FETCH_BUDGETS = "budget/fetchBudgets";
const SAVE_BUDGET = "budget/saveBudget";
const REMOVE_BUDGET = "budget/removeBudget";

export const fetchBudgets = createAsyncThunk(
  FETCH_BUDGETS,
  async (params = {}, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_BUDGETS, params);
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const saveBudget = createAsyncThunk(
  SAVE_BUDGET,
  async (payload, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(ADD_EDIT_BUDGET, {
        ID: payload.id || 0,
        ...payload,
      });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const removeBudget = createAsyncThunk(
  REMOVE_BUDGET,
  async ({ id, updatedBy }, thunkAPI) => {
    try {
      const response = await API_SERVICE.del(DELETE_BUDGET, { id, updatedBy });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

const CHECK_PO_ACTION = "budget/checkPO";
const REQUEST_OVERRIDE_ACTION = "budget/requestOverride";
const FETCH_PENDING_OVERRIDES = "budget/fetchPendingOverrides";
const DECIDE_OVERRIDE_ACTION = "budget/decideOverride";
const FETCH_OVERRIDE_HISTORY = "budget/fetchOverrideHistory";

export const checkPO = createAsyncThunk(
  CHECK_PO_ACTION,
  async (payload, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(CHECK_PO, payload);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const requestOverride = createAsyncThunk(
  REQUEST_OVERRIDE_ACTION,
  async (payload, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(REQUEST_OVERRIDE, payload);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const fetchPendingOverrides = createAsyncThunk(
  FETCH_PENDING_OVERRIDES,
  async (params = {}, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_PENDING_OVERRIDES, params);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const decideOverride = createAsyncThunk(
  DECIDE_OVERRIDE_ACTION,
  async (payload, thunkAPI) => {
    try {
      const response = await API_SERVICE.post(DECIDE_OVERRIDE, payload);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

export const fetchOverrideHistory = createAsyncThunk(
  FETCH_OVERRIDE_HISTORY,
  async (params = {}, thunkAPI) => {
    try {
      const response = await API_SERVICE.get(GET_OVERRIDES, params);
      return response.data.result;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  },
);

const initialState = {
  list: [],
  totalData: 0,
  status: "idle", // idle | loading | loaded | error
  checkResult: null,
  checkStatus: "idle", // idle | loading | loaded | error
  pendingOverrides: [],
  pendingOverridesTotalData: 0,
  pendingOverridesStatus: "idle",
  overrideHistory: [],
  overrideHistoryTotalData: 0,
  overrideHistoryStatus: "idle",
};

const budgetSlice = createSlice({
  name: "budget",
  initialState,
  reducers: {
    clearCheckResult: (state) => {
      state.checkResult = null;
      state.checkStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBudgets.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchBudgets.fulfilled, (state, action) => {
        state.list = action.payload.data || [];
        state.totalData = action.payload.totalData || 0;
        state.status = "loaded";
      })
      .addCase(fetchBudgets.rejected, (state) => {
        state.status = "error";
      })
      .addCase(checkPO.pending, (state) => {
        state.checkResult = null;
        state.checkStatus = "loading";
      })
      .addCase(checkPO.fulfilled, (state, action) => {
        state.checkResult = action.payload;
        state.checkStatus = "loaded";
      })
      .addCase(checkPO.rejected, (state) => {
        state.checkResult = null;
        state.checkStatus = "error";
      })
      .addCase(fetchPendingOverrides.pending, (state) => {
        state.pendingOverridesStatus = "loading";
      })
      .addCase(fetchPendingOverrides.fulfilled, (state, action) => {
        state.pendingOverrides = action.payload.data || [];
        state.pendingOverridesTotalData = action.payload.totalData || 0;
        state.pendingOverridesStatus = "loaded";
      })
      .addCase(fetchPendingOverrides.rejected, (state) => {
        state.pendingOverridesStatus = "error";
      })
      .addCase(fetchOverrideHistory.pending, (state) => {
        state.overrideHistoryStatus = "loading";
      })
      .addCase(fetchOverrideHistory.fulfilled, (state, action) => {
        state.overrideHistory = action.payload.data || [];
        state.overrideHistoryTotalData = action.payload.totalData || 0;
        state.overrideHistoryStatus = "loaded";
      })
      .addCase(fetchOverrideHistory.rejected, (state) => {
        state.overrideHistoryStatus = "error";
      });
  },
});

export const { clearCheckResult } = budgetSlice.actions;
export default budgetSlice.reducer;
